import { Metric, Metrics, Session, TypeMetricKey } from '@trace/types';
import { useContext, useEffect, useState } from 'react';
import { useQuery, UseQueryResult } from 'react-query';

import { ApiContext } from '../Api/Api';
import { URLStateContext } from '../URLState/URLState';
import { METRIC_BY_KEY } from '../../constants/Metrics';

type HookPayload = {
  isFetched: boolean;
  metrics: Partial<Record<TypeMetricKey, Metric<'raw'>>>;
  session: Session;
};
type HookResult = Pick<UseQueryResult, 'isError' | 'isFetching'> & HookPayload;

const useSession = (sessionId?: string): HookResult => {
  const { get } = useContext(ApiContext);
  const { demo } = useContext(URLStateContext);
  const [session, setSession] = useState<Session>({
    attributes: {},
    traces: [],
  });
  const [metrics, setMetrics] = useState({});

  const {
    data: dataSession,
    isError: isErrorSession,
    isFetching: isFetchingSession,
    isSuccess: isSuccessSession,
  } = useQuery<Session>(
    ['session', demo, sessionId],
    async () => get(`/traces?session=${sessionId}`),
    {
      enabled: !!sessionId,
    },
  );

  const {
    data: dataMetricCpuPct,
    isError: isErrorMetricCpuPct,
    isFetching: isFetchingMetricCpuPct,
    isSuccess: isSuccessMetricCpuPct,
  } = useQuery<Metrics<'raw'>>(['metricCpuPct', sessionId, dataSession], async () => {
    if (!dataSession) return undefined;

    return get(
      `/metrics/${METRIC_BY_KEY.CpuPct}?session=${sessionId}&start=${dataSession.start}&end=${dataSession.end}`,
    );
  });

  const {
    data: dataMetricMemoryBytes,
    isError: isErrorMetricMemoryBytes,
    isFetching: isFetchingMetricMemoryBytes,
    isSuccess: isSuccessMetricMemoryBytes,
  } = useQuery<Metrics<'raw'>>(['metricMemoryBytes', sessionId, dataSession], async () => {
    if (!dataSession) return undefined;

    return get(
      `/metrics/${METRIC_BY_KEY.MemoryBytes}?session=${sessionId}&start=${dataSession.start}&end=${dataSession.end}`,
    );
  });

  const isFetching = isFetchingSession || isFetchingMetricCpuPct || isFetchingMetricMemoryBytes;
  const isError = isErrorSession || isErrorMetricCpuPct || isErrorMetricMemoryBytes;

  /**
   * Use a side-effect to:
   *  - Update the `metrics` state, whenever the metrics queries resolve
   */
  useEffect(() => {
    if (dataSession) {
      setSession(dataSession);
    }
  }, [dataSession]);

  /**
   * Use a side-effect to:
   *  - Update the `metrics` state, whenever the metrics queries resolve
   */
  useEffect(() => {
    if (dataMetricCpuPct && dataMetricMemoryBytes) {
      setMetrics({
        CpuPct: dataMetricCpuPct[METRIC_BY_KEY.CpuPct],
        MemoryBytes: dataMetricMemoryBytes[METRIC_BY_KEY.MemoryBytes],
      });
    }
  }, [dataMetricCpuPct, dataMetricMemoryBytes]);

  return {
    session,
    metrics,
    isError,
    isFetching,
    isFetched: isSuccessSession && isSuccessMetricCpuPct && isSuccessMetricMemoryBytes,
  };
};

export default useSession;
